/**
 * The map echo — persisted half of the `map-echo` passive (issue #4).
 *
 * "Cartographer's Echo: the room graph of a failed run persists into the next
 * one on the same seed." That sentence needs a piece of state that outlives the
 * run which drew it, so it lives on the profile rather than in `RunState`.
 *
 * It is its own module, with **no imports at all**, for one specific reason:
 * `progression.ts` has to normalise it on load, and `passives.ts` — the natural
 * home for everything else about this passive — reads `UNLOCKS` at module scope.
 * A `progression → passives → progression` cycle would evaluate `PASSIVE_IDS`
 * against an undefined `UNLOCKS` depending on which module an entry point
 * happened to import first, which is the sort of failure that shows up once, in
 * production, as a blank screen. A leaf module cannot participate in a cycle.
 *
 * The seed key is the load-bearing part. Replaying room ids against a different
 * world would draw a map of rooms that are not there, which is strictly worse
 * than no map: the player would trust it.
 */

export interface MapEcho {
  /** The seed the graph was drawn on. Restoring against any other seed is a bug. */
  seed: number;
  /** Room ids visited. The map, minus the geometry, which regenerates from seed. */
  visited: string[];
  /** "from>to" edge keys, the same encoding `RoomGraph.edges` uses. */
  edges: string[];
}

/**
 * Cap, so a long run cannot grow the save without bound. A run that visits 400
 * rooms has already told the next one everything useful about the route.
 */
export const MAP_ECHO_MAX_ROOMS = 400;

export function makeMapEcho(
  seed: number,
  visited: Iterable<string>,
  edges: readonly string[],
): MapEcho {
  return {
    seed: seed >>> 0,
    visited: [...new Set(visited)].slice(0, MAP_ECHO_MAX_ROOMS),
    edges: [...new Set(edges)].slice(0, MAP_ECHO_MAX_ROOMS * 2),
  };
}

/** The echo, or null unless it was drawn on this exact seed. */
export function echoForSeed(echo: MapEcho | null | undefined, seed: number): MapEcho | null {
  if (!echo) return null;
  return echo.seed === (seed >>> 0) ? echo : null;
}

/**
 * Repair an echo loaded from disk. Never throws — a malformed echo is simply no
 * echo, which costs the player a map and nothing else.
 */
export function normaliseMapEcho(raw: unknown): MapEcho | null {
  if (!raw || typeof raw !== 'object') return null;
  const e = raw as Partial<MapEcho>;
  if (typeof e.seed !== 'number' || !Number.isFinite(e.seed)) return null;
  const visited = Array.isArray(e.visited) ? e.visited.filter((v) => typeof v === 'string') : [];
  const edges = Array.isArray(e.edges) ? e.edges.filter((v) => typeof v === 'string') : [];
  // An echo with no rooms is indistinguishable from no echo, and storing it
  // would make "has an echo" true while `visited` renders nothing.
  if (visited.length === 0) return null;
  return makeMapEcho(e.seed, visited, edges);
}
